import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useSettings } from '@/contexts/SettingsContext';
import { buildWhatsAppUrl } from '@/lib/utils';
import { trackEvent } from '@/lib/api';
import { Menu, X, Phone, MessageCircle, Car, Users, Mail, Shield } from 'lucide-react';

export function Header() {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const { settings } = useSettings();

    const navItems = [
        { href: '/', label: 'Inicio', icon: null },
        { href: '/coches', label: 'Vehículos', icon: Car },
        { href: '/nosotros', label: 'Nosotros', icon: Users },
        { href: '/contacto', label: 'Contacto', icon: Mail },
    ];

    const isActive = (href) => {
        if (href === '/') return location.pathname === '/';
        return location.pathname.startsWith(href);
    };

    const handleWhatsAppClick = () => {
        trackEvent({ event_type: 'whatsapp_click' }).catch(() => {});
    };

    return (
        <header className="sticky top-0 z-40 border-b bg-white/80 backdrop-blur-md" data-testid="main-header">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex h-16 items-center justify-between">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
                        <img 
                            src="/images/asg-logo.png" 
                            alt="ASG Motor" 
                            className="h-9 w-auto object-contain"
                        />
                    </Link>

                    {/* Desktop Nav */}
                    <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
                        {navItems.map((item) => (
                            <Link
                                key={item.href}
                                to={item.href}
                                data-testid={`nav-${item.label.toLowerCase()}-link`}
                                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                                    isActive(item.href)
                                        ? 'text-[var(--brand-navy)] bg-[var(--brand-light-blue)]'
                                        : 'text-[hsl(var(--muted-foreground))] hover:text-[var(--brand-navy)] hover:bg-[var(--brand-light-blue)]/50'
                                }`}
                            >
                                {item.label}
                            </Link>
                        ))}
                    </nav>

                    {/* Desktop CTA */}
                    <div className="hidden md:flex items-center gap-2">
                        <a
                            href={`tel:${settings?.phone || '+34600000000'}`}
                            data-testid="nav-call-button"
                            onClick={() => trackEvent({ event_type: 'call_click' }).catch(() => {})}
                        >
                            <Button variant="outline" size="sm" className="gap-1.5 border-[hsl(var(--border))] hover:bg-[hsl(var(--accent))]">
                                <Phone className="h-3.5 w-3.5" />
                                Llamar
                            </Button>
                        </a>
                        <a
                            href={buildWhatsAppUrl(settings)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="nav-whatsapp-button"
                            onClick={handleWhatsAppClick}
                        >
                            <Button size="sm" className="gap-1.5 bg-[#25D366] text-white hover:bg-[#20BD59] focus-visible:ring-[#25D366]/40">
                                <MessageCircle className="h-3.5 w-3.5" />
                                WhatsApp
                            </Button>
                        </a>
                        <Link to="/admin" data-testid="nav-admin-link">
                            <Button variant="ghost" size="sm" className="text-[hsl(var(--muted-foreground))]">
                                <Shield className="h-3.5 w-3.5" />
                            </Button>
                        </Link>
                    </div>

                    {/* Mobile Menu */}
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild className="md:hidden">
                            <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
                                <Menu className="h-5 w-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-72 bg-white p-0">
                            <div className="flex flex-col h-full">
                                <div className="flex items-center justify-between p-4 border-b">
                                    <span className="font-[var(--font-display)] text-lg font-semibold text-[var(--brand-navy)]">
                                        <img src="/images/asg-logo.png" alt="ASG Motor" className="h-8 w-auto" />
                                    </span>
                                </div>
                                <nav className="flex flex-col p-4 gap-1">
                                    {navItems.map((item) => (
                                        <Link
                                            key={item.href}
                                            to={item.href}
                                            onClick={() => setOpen(false)}
                                            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                                                isActive(item.href)
                                                    ? 'text-[var(--brand-navy)] bg-[var(--brand-light-blue)]'
                                                    : 'text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--accent))]'
                                            }`}
                                        >
                                            {item.icon && <item.icon className="h-4 w-4" />}
                                            {item.label}
                                        </Link>
                                    ))}
                                    <Link
                                        to="/admin"
                                        onClick={() => setOpen(false)}
                                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--accent))] mt-2 border-t pt-4"
                                    >
                                        <Shield className="h-4 w-4" />
                                        Admin
                                    </Link>
                                </nav>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
}
